import { inject, Injectable } from '@angular/core';
import { ConstructionProjectFeaturesAccessMap } from '@construction/construction.enums';
import { ConstructionProjectFeaturesType } from '@construction/models/construction-project.model';
import { TeamMember } from '@construction/modules/project-edit/projects.enum';
import { UserSignalStore } from '@core/stores/user-signal-store.service';

@Injectable({
  providedIn: 'root',
})
export class ConstructionProjectFeatureBuilderService {
  private userStore = inject(UserSignalStore);

  public buildPermissionMapForFeatures(params: {
    featureFlags: ConstructionProjectFeaturesType[];
    ownerCompanyId: string;
    epcCompanyId: string;
    supplierCompanyId: string;
    team: TeamMember[];
  }) {
    const { featureFlags, ownerCompanyId, epcCompanyId, supplierCompanyId, team } = params;
    const user = this.userStore.user();
    const userCompanyId = user?.company_profile?._id;
    const isUserFromOwnerCompany = userCompanyId === ownerCompanyId;
    const isUserFromEPCCompany = userCompanyId === epcCompanyId;
    const isUserFromSupplierCompany = userCompanyId === supplierCompanyId;
    const isSuperAdmin = this.userStore.isSuperAdmin();

    const featurePermissionMap: ConstructionProjectFeaturesAccessMap = {
      drone_imagery: false,
      advanced_detection: false,
      approvals: false,
      digital_twin: false,
      equipment_tracking: false,
      logistics_inventory: false,
      mv_circuit: false,
      omega: false,
      production_tracking: false,
      qc_issues: false,
      terafab: false,
      documents: false,
    };

    const isTeamMember = team.some((member) => member.contact_profile._id === user?.contact_profile._id);

    if (!isTeamMember && !isUserFromSupplierCompany && !isSuperAdmin) {
      return featurePermissionMap;
    }

    // Users from supplier companies don't have to be team members, epc and owner have to
    featureFlags.forEach((feature) => {
      // Superadmin sees a feature if it's enabled for either the Owner or the EPC company (PRD-995)
      if (isSuperAdmin) {
        featurePermissionMap[feature.name] = feature.owner_enabled || feature.epc_enabled;
      } else if (isTeamMember && isUserFromOwnerCompany) {
        featurePermissionMap[feature.name] = feature.owner_enabled;
      } else if (isTeamMember && isUserFromEPCCompany) {
        featurePermissionMap[feature.name] = feature.epc_enabled;
      } else if (isUserFromSupplierCompany) {
        featurePermissionMap[feature.name] = feature.supplier_enabled;
      }
    });

    return featurePermissionMap;
  }
}
